import React from 'react';

import type { Budget } from '../../services/api/assistants';

interface Props {
  budget: Budget;
}

// Mêmes colonnes, dans le même ordre, que la feuille produite.
const COLONNES = [
  'Code', 'Désignation', 'Unité', 'Quantité', 'Coût unitaire',
  'Montant', 'Source', 'Commentaire',
];

const SOURCES_MANQUANTES = ['à renseigner', 'a renseigner', 'non documenté', 'non documente'];

const sansAccents = (texte: string) =>
  texte.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const nombre = (valeur: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(valeur || 0);

/**
 * Aperçu de la feuille de calcul, avant téléchargement.
 *
 * La grille éditable sert à corriger ; celle-ci montre ce que le fichier
 * contiendra — mêmes colonnes, mêmes sous-totaux, même contre-valeur en
 * dollars. On ne télécharge pas pour découvrir ce qu'on a produit.
 *
 * Le contenu est reconstruit à partir des mêmes données que l'export, et non
 * lu depuis le fichier : la plateforme n'embarque pas de lecteur de tableur, et
 * en ajouter un pour un aperçu serait payer cher une fidélité au pixel.
 */
const ApercuTableur: React.FC<Props> = ({ budget }) => {
  const devise = budget.devise || 'GNF';
  const taux = budget.taux_change_gnf_usd;

  const total = (budget.rubriques || []).reduce(
    (somme, rubrique) =>
      somme
      + (rubrique.lignes || []).reduce(
        (sousTotal, ligne) => sousTotal + (ligne.quantite || 0) * (ligne.cout_unitaire || 0),
        0,
      ),
    0,
  );

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-eg-gris">
        Aperçu de la feuille telle qu'elle sera enregistrée. Les montants y sont des
        formules vivantes : le fichier recalcule si l'on modifie une quantité.
      </p>

      <div className="overflow-x-auto rounded-md border border-[#DDE2DD] bg-eg-blanc">
        <table className="min-w-[860px] w-full border-collapse text-[12px]">
          <caption className="border-b border-eg-bordure bg-eg-papier px-3 py-2 text-left
            text-[13px] font-semibold text-eg-encre">
            {budget.titre}
            <span className="ml-2 font-normal text-eg-gris">
              — montants en {devise}
            </span>
          </caption>

          <thead>
            <tr className="bg-[#1F4E79] text-[10px] uppercase tracking-[0.04em] text-eg-blanc">
              {COLONNES.map((colonne, rang) => (
                <th
                  key={colonne}
                  className={`px-2 py-1.5 font-semibold ${
                    rang >= 3 && rang <= 5 ? 'text-right' : 'text-left'
                  }`}
                >
                  {colonne}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {(budget.rubriques || []).map((rubrique, rangRubrique) => {
              const sousTotal = (rubrique.lignes || []).reduce(
                (somme, ligne) => somme + (ligne.quantite || 0) * (ligne.cout_unitaire || 0),
                0,
              );
              return (
                <React.Fragment key={rangRubrique}>
                  <tr className="bg-eg-voile">
                    <td colSpan={COLONNES.length}
                      className="px-2 py-1.5 font-semibold text-eg-brun">
                      {rubrique.nom}
                    </td>
                  </tr>

                  {(rubrique.lignes || []).map((ligne, rang) => {
                    // Une ligne sans source documentée est signalée : c'est
                    // elle qu'il faudra justifier devant un bailleur.
                    const aSourcer = SOURCES_MANQUANTES.includes(sansAccents(ligne.source || ''));
                    return (
                      <tr key={rang} className={`border-t border-eg-voile ${
                        aSourcer ? 'bg-eg-alerte/10' : ''
                      }`}>
                        <td className="px-2 py-1 font-mono text-[11px] text-eg-gris">{ligne.code}</td>
                        <td className="px-2 py-1 text-eg-encre">{ligne.designation}</td>
                        <td className="px-2 py-1 text-eg-gris">{ligne.unite}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{nombre(ligne.quantite)}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{nombre(ligne.cout_unitaire)}</td>
                        <td className="px-2 py-1 text-right font-semibold tabular-nums">
                          {nombre((ligne.quantite || 0) * (ligne.cout_unitaire || 0))}
                        </td>
                        <td className={`px-2 py-1 text-[11px] ${
                          aSourcer ? 'font-semibold text-eg-alerte' : 'text-eg-gris'
                        }`}>
                          {ligne.source}
                        </td>
                        <td className="px-2 py-1 text-[11px] text-eg-estompe">{ligne.commentaire}</td>
                      </tr>
                    );
                  })}

                  <tr className="border-t border-eg-bord bg-eg-papier">
                    <td />
                    <td className="px-2 py-1.5 font-semibold text-eg-brun" colSpan={4}>
                      Sous-total {rubrique.nom}
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-eg-encre">
                      {nombre(sousTotal)}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </React.Fragment>
              );
            })}

            <tr className="border-t-2 border-[#1F4E79] bg-[#1F4E79] text-eg-blanc">
              <td />
              <td className="px-2 py-2 font-bold uppercase tracking-wide" colSpan={4}>
                Total général
              </td>
              <td className="px-2 py-2 text-right font-bold tabular-nums">{nombre(total)}</td>
              <td colSpan={2} />
            </tr>

            {taux ? (
              <tr className="border-t border-eg-bord bg-eg-papier">
                <td />
                <td className="px-2 py-1.5 text-eg-gris" colSpan={4}>
                  Contre-valeur en dollars — taux BCRG {nombre(taux)}
                </td>
                <td className="px-2 py-1.5 text-right font-semibold tabular-nums text-eg-encre">
                  {new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })
                    .format(total / taux)} $
                </td>
                <td colSpan={2} />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {budget.hypotheses?.length > 0 && (
        <div className="rounded-md border border-eg-bord bg-eg-papier px-3 py-2.5">
          <p className="mb-1 text-[10px] uppercase tracking-[0.04em] text-eg-gris">
            Hypothèses retenues — reprises en tête de la feuille
          </p>
          <ul className="space-y-0.5 text-[12px] text-eg-encre">
            {budget.hypotheses.map((hypothese, rang) => (
              <li key={rang}>— {hypothese}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ApercuTableur;
